/**
 * Resource Request Planning Engine
 *
 * Given a ResourceRequest (hours budget + date range + role/skills),
 * this engine calls the backend plan-availability API which:
 *   1. Filters resources by role/skills/location match score
 *   2. Computes daily availability breakdowns from fresh DB data
 *
 * The frontend then builds 3 concrete PlanScenarios using different
 * optimisation strategies from the server-returned data:
 *
 *   1. single-resource  — concentrate all hours on the single best match
 *   2. spread-load      — spread across 2-3 resources to balance capacity
 *   3. fastest-start    — prioritise earliest availability over perfect fit
 *
 * All availability and resource filtering logic lives on the backend.
 */

import { Resource, Booking, WorkingHoursProfile } from '@/types';
import { ResourceRequest, PlanScenario, ProposedBookingSlot, DailyAllocation } from '@/types/resourceRequest';
import { resourceAvailabilityApi, type DailyAvailabilitySlot, type ResourcePlanResult } from '@/services/api';

// ── Helpers ───────────────────────────────────────────────────────────────────

function iso(d: Date) {
  return d.toISOString().split('T')[0];
}

function ym(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(ymStr: string) {
  const [y, m] = ymStr.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

function startOfMonth(ymStr: string) {
  const [y, m] = ymStr.split('-').map(Number);
  return new Date(y, m - 1, 1);
}

function endOfMonth(ymStr: string) {
  const [y, m] = ymStr.split('-').map(Number);
  return new Date(y, m, 0);
}

function monthsInRange(start: Date, end: Date): string[] {
  const months: string[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const endYm = ym(end);
  while (ym(cur) <= endYm) {
    months.push(ym(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
}

function slotId() {
  return `slot-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Convert server-returned DailyAvailabilitySlot[] into the distribution shape
 * used by the scenario builders, capping hours to assignedHours.
 *
 * Slots are sorted chronologically so the plan fills from the earliest
 * available date forward — producing the earliest feasible start date and
 * allocating a day's full hours before spilling to the next day.
 */
function distributeFromServerSlots(
  serverSlots: DailyAvailabilitySlot[],
  assignedHours: number,
  businessHoursStart: number,
  businessHoursEnd: number,
): { startDate: Date; endDate: Date; actualHours: number; dailyBreakdown: string[]; dailyAllocations: DailyAllocation[] } | null {
  if (serverSlots.length === 0 || assignedHours <= 0) return null;

  // Sort slots chronologically so the plan starts at the earliest available
  // date and fills each day before moving to the next.
  const sorted = [...serverSlots].sort((a, b) => {
    const dateCmp = a.date.localeCompare(b.date);
    if (dateCmp !== 0) return dateCmp;
    return a.startTime.localeCompare(b.startTime);
  });

  let remaining = assignedHours;
  const picked: { slot: DailyAvailabilitySlot; roundedHours: number }[] = [];

  for (const slot of sorted) {
    if (remaining <= 0) break;
    const hoursThisDay = Math.min(slot.hours, remaining);
    const roundedHours = Math.round(hoursThisDay * 10) / 10;
    if (roundedHours <= 0) continue;

    picked.push({ slot, roundedHours });
    remaining -= hoursThisDay;
  }

  if (picked.length === 0) return null;

  const breakdown: string[] = [];
  const allocations: DailyAllocation[] = [];
  let firstDay: Date | null = null;
  let lastDay: Date | null = null;

  for (const { slot, roundedHours } of picked) {
    const dayDate = new Date(slot.date + 'T00:00:00Z');
    if (!firstDay) firstDay = dayDate;
    lastDay = dayDate;

    // Use the slot's actual startTime and endTime — they reflect the exact
    // available window that does not overlap with existing bookings.
    // Fall back to businessHoursStart/End if no explicit times.
    const endHH = String(businessHoursEnd).padStart(2, '0');
    const fallbackStartHH = String(businessHoursStart).padStart(2, '0');
    const startTime = slot.startTime || `${slot.date}T${fallbackStartHH}:00:00Z`;

    const startMs = new Date(startTime).getTime();
    let endMs = startMs + roundedHours * 3600000;

    // Cap endTime at the slot's own endTime (end of free window) and
    // at businessHoursEnd — ensures we never overlap into a booking.
    if (slot.endTime) {
      const slotEnd = new Date(slot.endTime).getTime();
      if (endMs > slotEnd) endMs = slotEnd;
    }
    const bizEnd = new Date(`${slot.date}T${endHH}:00:00Z`).getTime();
    if (endMs > bizEnd) {
      endMs = bizEnd;
    }
    const endTime = new Date(endMs).toISOString();

    breakdown.push(`${dayDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}: ${roundedHours}h`);
    allocations.push({ date: slot.date, hours: roundedHours, startTime, endTime });
  }

  if (!firstDay || !lastDay) return null;

  return {
    startDate: firstDay,
    endDate: lastDay,
    actualHours: assignedHours - Math.max(0, remaining),
    dailyBreakdown: breakdown,
    dailyAllocations: allocations,
  };
}

/**
 * Filter server slots to only those within a specific month range.
 */
function slotsForMonth(slots: DailyAvailabilitySlot[], monthKey: string): DailyAvailabilitySlot[] {
  const mStart = startOfMonth(monthKey);
  const mEnd = endOfMonth(monthKey);
  return slots.filter(s => {
    const d = new Date(s.date + 'T00:00:00');
    return d >= mStart && d <= mEnd;
  });
}

// ── Local fallback: compute ResourcePlanResult[] from frontend mock data ─────
// Used when the backend is unavailable (e.g. demo / frontend-only mode).
// Scores resources by role+skills match, then builds a day-by-day breakdown
// of available hours by subtracting existing bookings from the working profile.

interface LocalPlanInput {
  request: ResourceRequest;
  resources: Resource[];
  existingBookings: Booking[];
  globalWorkingProfile: WorkingHoursProfile;
  businessHoursStart: number;
  businessHoursEnd: number;
  requestStart: Date;
  requestEnd: Date;
}

function computeLocalPlanResult(input: LocalPlanInput): ResourcePlanResult[] {
  const { request, resources, existingBookings, globalWorkingProfile, businessHoursStart, businessHoursEnd, requestStart, requestEnd } = input;

  const businessWindowHours = businessHoursEnd - businessHoursStart;

  // Day names mapping (Date.getDay() → profile key)
  const DAY_KEYS: (keyof WorkingHoursProfile)[] = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
  ];

  // ── Score each resource using the soft-signal model ──────────────────────
  function scoreResource(r: Resource): number {
    let signals = 0;

    // Role match (exact or substring overlap)
    const requestRoleLower = request.role.toLowerCase();
    const resourceRoleLower = (r.role ?? '').toLowerCase();
    if (
      resourceRoleLower === requestRoleLower ||
      requestRoleLower.split(' ').some(word => resourceRoleLower.includes(word)) ||
      resourceRoleLower.split(' ').some(word => requestRoleLower.includes(word))
    ) {
      signals++;
    }

    // Skills match (all required skills covered)
    const resourceSkills = (r.skills ?? []).map(s => s.name.toLowerCase());
    const requestSkills = request.skills.map(s => s.toLowerCase());
    if (requestSkills.length === 0 || requestSkills.every(s => resourceSkills.includes(s))) {
      signals++;
    }

    // Utilisation — always met in local fallback (no prior utilisation data)
    signals++;

    return signals;
  }

  // ── Build daily breakdown for a resource in [requestStart, requestEnd] ──
  function buildDailyBreakdown(r: Resource): DailyAvailabilitySlot[] {
    const profile = r.useCustomWorkingProfile && r.customWorkingProfile
      ? r.customWorkingProfile
      : globalWorkingProfile;

    // Collect busy intervals from existing bookings for this resource
    const resourceBookings = existingBookings.filter(
      b => b.resourceId === r.id || (b.resourceIds ?? []).includes(r.id),
    );

    const slots: DailyAvailabilitySlot[] = [];

    const cur = new Date(requestStart.getFullYear(), requestStart.getMonth(), requestStart.getDate());
    const endDay = new Date(requestEnd.getFullYear(), requestEnd.getMonth(), requestEnd.getDate());

    while (cur <= endDay) {
      const dayKey = DAY_KEYS[cur.getDay()];
      const contractedHours = (profile as unknown as Record<string, number>)[dayKey as string] ?? 0;

      if (contractedHours > 0) {
        const dateStr = iso(cur);
        // Clamp the slot to the configured business-hours window so
        // generated plan bookings start/end within business hours.
        const startHH = String(businessHoursStart).padStart(2, '0');
        const endHH = String(businessHoursEnd).padStart(2, '0');
        const dayStart = `${dateStr}T${startHH}:00:00Z`;
        const dayEnd = `${dateStr}T${endHH}:00:00Z`;

        // Sum booked hours on this day for this resource
        let bookedHours = 0;
        for (const bk of resourceBookings) {
          try {
            const bkStart = new Date(bk.startTime);
            const bkEnd = new Date(bk.endTime);
            const dayMidnight = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate());
            const nextMidnight = new Date(dayMidnight.getTime() + 86400000);
            if (bkEnd > dayMidnight && bkStart < nextMidnight) {
              const overlapStart = Math.max(bkStart.getTime(), dayMidnight.getTime());
              const overlapEnd = Math.min(bkEnd.getTime(), nextMidnight.getTime());
              bookedHours += (overlapEnd - overlapStart) / 3600000;
            }
          } catch { /* skip malformed bookings */ }
        }

        const availableHours = Math.min(
          Math.max(0, contractedHours - bookedHours),
          businessWindowHours,
        );

        slots.push({
          date: dateStr,
          startTime: dayStart,
          endTime: dayEnd,
          hours: availableHours,
          availableHours,
        });
      }

      cur.setDate(cur.getDate() + 1);
    }

    return slots;
  }

  // ── Build and rank results ─────────────────────────────────────────────
  const results: ResourcePlanResult[] = [];

  for (const r of resources) {
    if (r.id === 'unallocated') continue;

    const score = scoreResource(r);

    const dailyBreakdown = buildDailyBreakdown(r);
    const totalAvailable = dailyBreakdown.reduce((sum, s) => sum + s.availableHours, 0);
    if (totalAvailable <= 0) continue;

    // Resource IDs are strings like '1', '17' — parse to number; fall back to index
    const numericId = parseInt(r.id, 10);
    results.push({
      resourceId: isNaN(numericId) ? results.length + 1 : numericId,
      resourceName: r.name,
      resourceRole: r.role ?? '',
      matchScore: score,
      totalAvailableHours: totalAvailable,
      dailyBreakdown,
    });
  }

  // Sort by soft signals met descending
  results.sort((a, b) => b.matchScore - a.matchScore);

  return results;
}

// ── Main planning function ─────────────────────────────────────────────────────

export interface PlannerInput {
  request: ResourceRequest;
  resources: Resource[];
  existingBookings: Booking[];
  globalWorkingProfile: WorkingHoursProfile;
  businessHoursStart: number;
  businessHoursEnd: number;
}

export async function generatePlanScenarios(input: PlannerInput): Promise<PlanScenario[]> {
  const { request, businessHoursStart, businessHoursEnd } = input;

  const requestStart = new Date(request.requestedStartDate);
  let requestEnd = request.requestedEndDate
    ? new Date(request.requestedEndDate)
    : (() => {
        const d = new Date(requestStart);
        d.setMonth(d.getMonth() + 2);
        d.setDate(endOfMonth(ym(d)).getDate());
        return d;
      })();

  // Extend end date to cover all months referenced by monthly hour hints
  if (request.monthlyHourHints) {
    const hintMonths = Object.keys(request.monthlyHourHints).sort();
    if (hintMonths.length > 0) {
      const lastHintMonth = hintMonths[hintMonths.length - 1];
      const lastHintEnd = endOfMonth(lastHintMonth);
      if (lastHintEnd > requestEnd) {
        requestEnd = lastHintEnd;
      }
    }
  }

  const months = monthsInRange(requestStart, requestEnd);
  const totalHours = request.totalHoursRequested;

  // ── Try backend; fall back to local mock computation ────────────────────
  let planResult: ResourcePlanResult[];
  try {
    const response = await resourceAvailabilityApi.planAvailability({
      startDate: requestStart.toISOString(),
      endDate: requestEnd.toISOString(),
      totalHours,
      role: request.role,
      skills: request.skills,
      location: request.location,
      businessHoursStart,
      businessHoursEnd,
    });
    planResult = response.resources;
  } catch (_err) {
    // Backend unavailable — compute availability from local mock data
    console.info('[planAvailability] Backend unavailable — using local mock fallback');
    planResult = computeLocalPlanResult({
      request,
      resources: input.resources,
      existingBookings: input.existingBookings,
      globalWorkingProfile: input.globalWorkingProfile,
      businessHoursStart,
      businessHoursEnd,
      requestStart,
      requestEnd,
    });
  }

  if (planResult.length === 0) return [];

  // ── Split daily slots into free time windows around existing bookings ────
  // The server returns one slot per day with total available hours but does
  // not indicate WHERE in the day existing bookings sit. We use
  // input.existingBookings to compute the actual free time windows within
  // each day's working window, so plan allocations never overlap bookings.
  //
  // For days with no bookings the full contracted hours (from profile) are
  // used as a single window.
  const DAY_KEYS_MAIN: (keyof WorkingHoursProfile)[] = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
  ];

  for (const r of planResult) {
    const frontendResource = input.resources.find(
      res => String(res.id) === String(r.resourceId),
    );
    if (!frontendResource) continue;

    const profile = frontendResource.customWorkingProfile;
    if (!profile) continue;

    // Collect existing bookings for this resource
    const resBookings = input.existingBookings.filter(
      bk => String(bk.resourceId) === String(frontendResource.id),
    );

    const newBreakdown: DailyAvailabilitySlot[] = [];

    for (const slot of r.dailyBreakdown) {
      const dayOfWeek = new Date(slot.date + 'T00:00:00').getDay();
      const dayKey = DAY_KEYS_MAIN[dayOfWeek];
      const contracted = (profile as unknown as Record<string, number>)[dayKey as string] ?? 0;
      if (contracted <= 0) continue;

      // Working window for this day
      const startHH = String(businessHoursStart).padStart(2, '0');
      const endHH = String(businessHoursEnd).padStart(2, '0');
      const windowStart = new Date(`${slot.date}T${startHH}:00:00Z`).getTime();
      const windowEnd = new Date(`${slot.date}T${endHH}:00:00Z`).getTime();

      // Find bookings that overlap this day's working window
      const dayBookings: { start: number; end: number }[] = [];
      for (const bk of resBookings) {
        try {
          const bkStart = new Date(bk.startTime).getTime();
          const bkEnd = new Date(bk.endTime).getTime();
          if (bkEnd > windowStart && bkStart < windowEnd) {
            dayBookings.push({
              start: Math.max(bkStart, windowStart),
              end: Math.min(bkEnd, windowEnd),
            });
          }
        } catch { /* skip malformed */ }
      }

      if (dayBookings.length === 0) {
        // No bookings — full contracted hours available as single window
        newBreakdown.push({
          date: slot.date,
          startTime: `${slot.date}T${startHH}:00:00Z`,
          endTime: `${slot.date}T${endHH}:00:00Z`,
          hours: contracted,
          availableHours: contracted,
        });
      } else {
        // Sort bookings by start time and compute free gaps
        dayBookings.sort((a, b) => a.start - b.start);

        // Merge overlapping bookings
        const merged: { start: number; end: number }[] = [];
        for (const bk of dayBookings) {
          if (merged.length > 0 && bk.start <= merged[merged.length - 1].end) {
            merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, bk.end);
          } else {
            merged.push({ ...bk });
          }
        }

        // Build free windows between bookings
        let cursor = windowStart;
        for (const bk of merged) {
          if (bk.start > cursor) {
            const gapHours = (bk.start - cursor) / 3600000;
            if (gapHours > 0) {
              newBreakdown.push({
                date: slot.date,
                startTime: new Date(cursor).toISOString(),
                endTime: new Date(bk.start).toISOString(),
                hours: Math.round(gapHours * 10) / 10,
                availableHours: Math.round(gapHours * 10) / 10,
              });
            }
          }
          cursor = Math.max(cursor, bk.end);
        }
        // Gap after last booking until end of working window
        if (cursor < windowEnd) {
          const gapHours = (windowEnd - cursor) / 3600000;
          if (gapHours > 0) {
            newBreakdown.push({
              date: slot.date,
              startTime: new Date(cursor).toISOString(),
              endTime: new Date(windowEnd).toISOString(),
              hours: Math.round(gapHours * 10) / 10,
              availableHours: Math.round(gapHours * 10) / 10,
            });
          }
        }
      }
    }

    r.dailyBreakdown = newBreakdown;
  }

  // Build per-resource per-month availability from server response
  type AvailMap = Record<number, Record<string, number>>;
  const availMap: AvailMap = {};
  type BreakdownCache = Record<number, Record<string, DailyAvailabilitySlot[]>>;
  const breakdownCache: BreakdownCache = {};

  for (const r of planResult) {
    availMap[r.resourceId] = {};
    breakdownCache[r.resourceId] = {};
    for (const monthKey of months) {
      const monthSlots = slotsForMonth(r.dailyBreakdown, monthKey);
      breakdownCache[r.resourceId][monthKey] = monthSlots;
      availMap[r.resourceId][monthKey] = monthSlots.reduce((sum, s) => sum + s.availableHours, 0);
    }
  }

  // ── Helper: build signal reason strings ──────────────────────────────────
  function matchReasons(softSignalsMet: number, resourceRole: string): string[] {
    const reasons: string[] = [];
    reasons.push(`${softSignalsMet} of 3 criteria met`);
    if (resourceRole) reasons.push(`Role: ${resourceRole}`);
    if (request.skills.length > 0) reasons.push(`Skills: ${request.skills.slice(0, 3).join(', ')}`);
    return reasons;
  }

  // ── Strategy 1: Single Resource ──────────────────────────────────────────
  function buildSingleResourceScenario(): PlanScenario | null {
    for (const resource of planResult) {
      const rid = resource.resourceId;
      let remainingHours = totalHours;
      const slots: ProposedBookingSlot[] = [];

      for (const monthKey of months) {
        if (remainingHours <= 0) break;
        const avail = availMap[rid]?.[monthKey] ?? 0;
        if (avail <= 0) continue;

        const hintHours = request.monthlyHourHints?.[monthKey];
        const maxThisMonth = hintHours ?? Math.min(avail, remainingHours);

        const serverSlots = breakdownCache[rid]?.[monthKey] ?? [];
        const distribution = distributeFromServerSlots(serverSlots, maxThisMonth, businessHoursStart, businessHoursEnd);

        if (distribution) {
          const reasons = matchReasons(resource.matchScore, resource.resourceRole);
          reasons.push(...distribution.dailyBreakdown);

          slots.push({
            id: slotId(),
            resourceId: String(rid),
            resourceName: resource.resourceName,
            resourceRole: resource.resourceRole,
            resourcePhotoUrl: undefined,
            softSignalsMet: resource.matchScore,
            month: monthLabel(monthKey),
            monthKey,
            startDate: iso(distribution.startDate),
            endDate: iso(distribution.endDate),
            hours: distribution.actualHours,
            availableHours: Math.round(avail),
            matchReasons: reasons,
            dailyBreakdown: distribution.dailyAllocations,
          });
          remainingHours -= distribution.actualHours;
        }
      }

      if (slots.length > 0) {
        const covered = totalHours - remainingHours;
        return {
          id: 'sc-single-resource',
          strategy: 'single-resource',
          label: 'Single Resource',
          description: `All ${covered}h assigned to ${resource.resourceName} — simplest coordination`,
          icon: 'User',
          totalHours: covered,
          slots,
          coveragePercent: Math.round((covered / totalHours) * 100),
          resourceCount: 1,
          earliestStart: slots[0].startDate,
          latestEnd: slots[slots.length - 1].endDate,
          pros: [
            'Single point of contact — simplest communication',
            `${resource.resourceName} is the best match for required skills`,
            'Consistent quality — one person owns the deliverable end-to-end',
          ],
          cons: [
            'Single point of failure if the resource becomes unavailable',
            'May overload the resource compared to spreading the work',
          ],
        };
      }
    }
    return null;
  }

  // ── Strategy 2: Spread Load ──────────────────────────────────────────────
  function buildSpreadLoadScenario(): PlanScenario | null {
    const slots: ProposedBookingSlot[] = [];
    let remainingHours = totalHours;

    for (const monthKey of months) {
      if (remainingHours <= 0) break;

      const hintHours = request.monthlyHourHints?.[monthKey];
      let monthBudget = hintHours ?? Math.ceil(remainingHours / Math.max(1, months.length - months.indexOf(monthKey)));
      monthBudget = Math.min(monthBudget, remainingHours);

      // Spread across multiple resources proportionally to their availability
      const resourcesWithAvail = planResult
        .map(r => ({ resource: r, avail: availMap[r.resourceId]?.[monthKey] ?? 0 }))
        .filter(x => x.avail > 0);

      const totalAvailThisMonth = resourcesWithAvail.reduce((s, x) => s + x.avail, 0);
      if (totalAvailThisMonth === 0) continue;

      // Pre-compute shares for each resource, ensuring the full budget is allocated.
      // Use floor for all except the last resource which gets the remainder.
      const shares: number[] = [];
      let allocated = 0;
      for (let i = 0; i < resourcesWithAvail.length; i++) {
        const { avail } = resourcesWithAvail[i];
        const proportion = avail / totalAvailThisMonth;
        if (i === resourcesWithAvail.length - 1) {
          // Last resource gets whatever remains of the budget
          shares.push(Math.min(monthBudget - allocated, avail));
        } else {
          const share = Math.min(Math.ceil(monthBudget * proportion), avail, monthBudget - allocated);
          shares.push(share);
          allocated += share;
        }
      }

      for (let i = 0; i < resourcesWithAvail.length; i++) {
        if (monthBudget <= 0) break;
        const { resource, avail } = resourcesWithAvail[i];
        const rid = resource.resourceId;

        const assignedHours = Math.min(shares[i], monthBudget);
        if (assignedHours <= 0) continue;

        const serverSlots = breakdownCache[rid]?.[monthKey] ?? [];
        const distribution = distributeFromServerSlots(serverSlots, assignedHours, businessHoursStart, businessHoursEnd);

        if (distribution) {
          const reasons = matchReasons(resource.matchScore, resource.resourceRole);
          reasons.push(...distribution.dailyBreakdown);

          slots.push({
            id: slotId(),
            resourceId: String(rid),
            resourceName: resource.resourceName,
            resourceRole: resource.resourceRole,
            resourcePhotoUrl: undefined,
            softSignalsMet: resource.matchScore,
            month: monthLabel(monthKey),
            monthKey,
            startDate: iso(distribution.startDate),
            endDate: iso(distribution.endDate),
            hours: distribution.actualHours,
            availableHours: Math.round(avail),
            matchReasons: reasons,
            dailyBreakdown: distribution.dailyAllocations,
          });
          monthBudget -= distribution.actualHours;
          remainingHours -= distribution.actualHours;
        }
      }
    }

    if (slots.length === 0) return null;

    const covered = totalHours - remainingHours;
    const uniqueResources = new Set(slots.map(s => s.resourceId)).size;

    return {
      id: 'sc-spread-load',
      strategy: 'spread-load',
      label: 'Spread the Load',
      description: `Hours distributed across ${uniqueResources} resources to balance capacity`,
      icon: 'Users',
      totalHours: covered,
      slots,
      coveragePercent: Math.round((covered / totalHours) * 100),
      resourceCount: uniqueResources,
      earliestStart: slots[0].startDate,
      latestEnd: slots[slots.length - 1].endDate,
      pros: [
        `${uniqueResources} resources share the workload — reduces individual pressure`,
        'Better risk coverage if one resource becomes unavailable',
        'Leverages the most available capacity across the team',
      ],
      cons: [
        'Requires coordination and knowledge sharing between resources',
        'Slightly higher management overhead',
      ],
    };
  }

  // ── Strategy 3: Fastest Start ────────────────────────────────────────────
  function buildFastestStartScenario(): PlanScenario | null {
    const slots: ProposedBookingSlot[] = [];
    let remainingHours = totalHours;

    for (const monthKey of months) {
      if (remainingHours <= 0) break;

      // Sort by available hours this month (highest first) rather than match score
      const byAvailability = planResult
        .map(r => ({ resource: r, avail: availMap[r.resourceId]?.[monthKey] ?? 0 }))
        .filter(x => x.avail > 0)
        .sort((a, b) => b.avail - a.avail);

      if (byAvailability.length === 0) continue;

      const hintHours = request.monthlyHourHints?.[monthKey];
      let monthBudget = hintHours ?? Math.ceil(remainingHours / Math.max(1, months.length - months.indexOf(monthKey)));
      monthBudget = Math.min(monthBudget, remainingHours);

      for (const { resource, avail } of byAvailability) {
        if (monthBudget <= 0) break;
        const rid = resource.resourceId;
        const assignedHours = Math.min(monthBudget, avail);
        if (assignedHours <= 0) continue;

        const serverSlots = breakdownCache[rid]?.[monthKey] ?? [];
        const distribution = distributeFromServerSlots(serverSlots, assignedHours, businessHoursStart, businessHoursEnd);

        if (distribution) {
          slots.push({
            id: slotId(),
            resourceId: String(rid),
            resourceName: resource.resourceName,
            resourceRole: resource.resourceRole,
            resourcePhotoUrl: undefined,
            softSignalsMet: resource.matchScore,
            month: monthLabel(monthKey),
            monthKey,
            startDate: iso(distribution.startDate),
            endDate: iso(distribution.endDate),
            hours: distribution.actualHours,
            availableHours: Math.round(avail),
            matchReasons: [
              `Highest availability this month (${Math.round(avail)}h free)`,
              `${resource.matchScore} of 3 criteria met`,
              resource.resourceRole,
              ...distribution.dailyBreakdown,
            ],
            dailyBreakdown: distribution.dailyAllocations,
          });
          monthBudget -= distribution.actualHours;
          remainingHours -= distribution.actualHours;
        }
      }
    }

    if (slots.length === 0) return null;

    const covered = totalHours - remainingHours;
    const uniqueResources = new Set(slots.map(s => s.resourceId)).size;

    return {
      id: 'sc-fastest-start',
      strategy: 'fastest-start',
      label: 'Fastest Start',
      description: 'Maximises availability — gets work started as soon as possible',
      icon: 'Zap',
      totalHours: covered,
      slots,
      coveragePercent: Math.round((covered / totalHours) * 100),
      resourceCount: uniqueResources,
      earliestStart: slots[0].startDate,
      latestEnd: slots[slots.length - 1].endDate,
      pros: [
        'Work begins at the earliest possible date',
        'Maximises use of available capacity right now',
        'Ideal when the project start date is non-negotiable',
      ],
      cons: [
        'May assign resources with slightly lower skill match scores',
        'Availability-first approach may not respect role preferences strictly',
      ],
    };
  }

  const scenarios: PlanScenario[] = [];

  const single = buildSingleResourceScenario();
  if (single) scenarios.push(single);

  const spread = buildSpreadLoadScenario();
  if (spread) scenarios.push(spread);

  const fastest = buildFastestStartScenario();
  if (fastest) scenarios.push(fastest);

  return scenarios;
}


